import { date } from 'quasar'
const { addToDate } = date
import Generic from 'src/models/generic/Generic'
import Model, { encoders, ID, Integer, IsoDate, IsoDateTime, JsonObject, MODEL_LEVEL } from 'src/models/model'
import TaskPlanning, { ARRIVE_CHART_WIDTH } from 'src/models/planning/TaskPlanning'
import BookingWeekMovie from 'src/models/schedule/BookingWeekMovie'
import PlanElement from 'src/models/schedule/PlanElement'
import ShowingSlot, { totalItemHeight } from 'src/models/schedule/ShowingSlot'
import SiteSchedule from 'src/models/schedule/SiteSchedule'
import { START_HOUR, timeToMinutes } from 'src/models/schedule/TimeSlot'
import Showing from 'src/models/showing/Showing'

export const resourceGraphHeight = 102
export const maxIngressHeatMapValue = 25
export const maxEgressHeatMapValue = 2
export const maxTotalResourceHeatMapValue = 5
export const resourceResolutionMinutes = 10

export function showingSlotKey (screenId, timeSlotId, dayNumber) {
  return `${screenId},${timeSlotId},${dayNumber}`
}

export function showingSlotKeyDecode (key) {
  const values = key.split(',')

  return {
    screenId: values[0],
    timeSlotId: values[1],
    dayNumber: values[2]
  }
}

// Round robin math for day numbers. Supports negative and positive overflows.
export function validateDayNumber (dayNumber) {
  return (dayNumber + 7) % 7
}

export default class BookingWeek extends Model {
  static name = 'BookingWeek'

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmProgramming]

  static define () {
    this.paginate = true

    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      date: {
        type: IsoDate,
        sortable: true
      },
      period: {
        type: String,
        output: true
      },
      weekName: {
        type: String,
        output: true
      },
      planElements: {
        type: [PlanElement],
        allowDestroy: true,
        encode: encoders.if(function () { return !this.preventEncodingPlanElements }),
        default: () => { return [] }
      },
      defaultSiteSchedule: {
        type: SiteSchedule,
        output: true
      },
      customSiteSchedule: {
        type: JsonObject,
        default: () => { return {} }
      },
      config: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            manualSiteSchedule: false,
            manualLayout: false,
            allowSimultaneousScreenings: false,
            screenTitleOneTwice: false,
            rotateTimesOnSplitScreens: false
          }
        }
      },
      customLayout: {
        type: JsonObject,
        default: () => { return {} }
      },
      notes: {
        type: String,
        default: null
      },
      preventResults: {
        type: Boolean,
        input: true,
        default: false
      },
      customLayoutMap: {
        type: Object,
        local: true,
        trackChanges: true,
        default: () => { return {} }
      },
      showings: {
        type: [Showing],
        allowDestroy: true,
        default: () => { return [] }
      },
      taskPlanningForWeek: {
        type: [TaskPlanning],
        default: () => ([]),
        local: true
      },

      // UI Related
      previewLayout: {
        type: Object,
        local: true,
        default: () => { return {} }
      },
      previewingAction: {
        type: Boolean,
        local: true,
        default: false
      },
      layoutMode: {
        type: String,
        local: true,
        default: 'plan-and-actual' // 'plan', 'plan-and-actual', 'actual'
      },
      scheduleMode: {
        type: String,
        local: true,
        default: 'plan-and-actual' // 'plan-and-actual', 'actual'
      },
      pixelsPerMinute: {
        type: Number,
        default: 1,
        local: true
      },
      screenFilter: {
        type: String,
        default: null,
        local: true
      },
      dayFilter: {
        type: Integer,
        default: 5, // Default to Friday, TODO: use default from circuit
        local: true
      },
      by: {
        type: String,
        default: 'day', // 'screen', 'day',
        local: true
      },
      showResources: {
        type: Boolean,
        default: false,
        local: true
      },
      snapTo: {
        type: Integer,
        default: 5,
        local: true
      },
      overrideLock: {
        type: Boolean,
        default: false,
        local: true
      },
      computedLayout: {
        type: Object,
        default () {
          return {}
        },
        local: true,
        skipProxyPropagation: true
      },
      actualSchedule: {
        type: Array,
        default () {
          return []
        },
        local: true,
        skipProxyPropagation: true
      },
      auditView: {
        type: String,
        default: null,
        local: true
      },
      paintValue: {
        type: Object,
        default: null,
        local: true
      },
      showingSlotGhost: {
        type: Object,
        default: null,
        local: true
      },
      preventEncodingPlanElements: {
        type: Boolean,
        default: false,
        local: true
      },
      batchSavingInProgress: {
        type: Boolean,
        default: false,
        local: true
      },
      copyFromId: {
        type: ID,
        default: null,
        input: true
      }
    }

    this.queries = {
      bookingWeekCopySummary: {
        arguments: {
          copyFrom: {
            type: ID,
            required: true
          },
          copyTo: {
            type: ID,
            required: true
          }
        },
        results: {
          summary: Generic
        }
      },
      bookingWeekCopyOptions: {
        arguments: {
          siteId: {
            type: ID,
            required: true
          },
          fromDate: {
            type: String,
            required: false
          }
        },
        results: {
          summary: Generic
        }
      }
    }

    this.computed = {
      allowShowResources () {
        return this.by === 'day' && this.dayFilter !== null
      },
      taskPlanningForDayFilter () {
        if (!this.allowShowResources) return null

        return this.taskPlanningForWeek.data[0]
      },
      daysOfWeekNumbers () {
        if (this.by !== 'day' || this.dayFilter === null) {
          return this.configuration.currentCircuit.daysOfWeekNumbers
        }
        return [this.dayFilter]
      },
      filteredScreens () {
        if (this.auditView === 'start-times' && this.paintValue) {
          // Filter to screens with the paint by movie value
          return _.filter(
            this.configuration.screens.data,
            screen =>
              _.some(
                this.showings.data,
                showing =>
                  +showing.dayNumber === this.dayFilter &&
                  showing.screenId === screen.id &&
                  showing.movie.id === this.paintValue.movie.id
              )
          )
        }

        if (this.by !== 'screen' || this.screenFilter === null) {
          return this.configuration.screens.data
        }

        return _.filter(this.configuration.screens.data, screen => screen.id === this.screenFilter)
      },
      selected () {
        return _.filter(this.completeSchedule, { selected: true })
      },
      selectedWithShowings () {
        return _.filter(this.selected, showingSlot => !!showingSlot.showing)
      },
      selectedWithoutShowings () {
        return _.filter(this.selected, showingSlot => !showingSlot.showing)
      },
      selectedWithEditableTime () {
        if (_.some(this.selected, showingSlot => showingSlot.showing && showingSlot.showing.loading)) return []

        return _.filter(this.selected, showingSlot => showingSlot.showing && !showingSlot.showing.loading && (this.overrideLock || (!showingSlot.showing.published && showingSlot.showing.ticketsSold === 0)))
      },
      startDayNumber () {
        return this.date.getDay()
      },
      indexOfDayFilter () {
        if (this.dayFilter === null) return 0

        return this.configuration.currentCircuit.daysOfWeekNumbers.indexOf(this.dayFilter)
      },
      dateOfDayFilter () {
        if (this.dayFilter === null) return this.date

        return date.addToDate(this.date, { days: this.configuration.currentCircuit.daysOfWeekNumbers.indexOf(this.dayFilter) })
      },
      canConvertToShowings () {
        return _.some(this.selected, showingSlot => !showingSlot.showing && (showingSlot.movie && showingSlot.movie.isRemote))
      },
      canPublishShowings () {
        return _.some(this.selected, showingSlot => showingSlot.showing && !showingSlot.showing.published)
      },
      canUnpublishShowings () {
        return _.some(this.selected, showingSlot => showingSlot.showing && showingSlot.showing.published)
      },
      // Don't allow time alignment if multiple showings selected in the same screen and day
      canAlignTime () {
        return this.selectedWithEditableTime.length && _.uniqBy(this.selectedWithEditableTime, showingSlot => `${showingSlot.screen.id},${showingSlot.dayNumber}`).length === this.selectedWithEditableTime.length
      },
      // Don't allow time alignment if multiple showings selected in the same screen and day
      canSpaceEvenly () {
        return !!_(this.selectedWithEditableTime).groupBy(showingSlot => `${showingSlot.screen.id},${showingSlot.dayNumber}`).find(groupedShowingSlots => groupedShowingSlots.length > 2)
      },
      totalDemand () {
        const egressValues = {}

        if (this.allowShowResources) {
          const taskPlanning = this.taskPlanningForDayFilter
          for (let intervalIndex = 0; intervalIndex < 24 * 60 / resourceResolutionMinutes; intervalIndex++) {
            const startIndex = intervalIndex * resourceResolutionMinutes
            const values = taskPlanning.employeeDemandSeries.slice(startIndex - timeToMinutes(taskPlanning.siteStartTime), startIndex - timeToMinutes(taskPlanning.siteStartTime) + resourceResolutionMinutes)
            const value = values ? _.sum(values) : 0
            egressValues[intervalIndex] = values.length === 0 ? 0 : Math.round(value / values.length * 10) / 10
          }
        }

        return egressValues
      },
      totalResourceIntervals () {
        const totalResourceIntervals = []
        const egressIntervals = []
        if (!this.showResources || this.by !== 'day') {
          return egressIntervals
        }

        if (this.allowShowResources) {
          for (let intervalIndex = 0; intervalIndex < 24 * 60 / resourceResolutionMinutes; intervalIndex++) {
            const value = this.totalDemand[intervalIndex] || 0
            const colorValue = 100 - Math.min(value * 100 / maxTotalResourceHeatMapValue, 100)
            const top = 40 + resourceGraphHeight

            totalResourceIntervals.push({
              top: top + 'px',
              left: intervalIndex * resourceResolutionMinutes * this.pixelsPerMinute + 'px',
              width: resourceResolutionMinutes * this.pixelsPerMinute + 'px',
              backgroundColor: 'hsl(' + colorValue + ', 65%, 60%)',
              color: 'hsl(' + colorValue + ', 65%, 40%)',
              value: this.pixelsPerMinute < 1.75 ? '' : value
            })
          }
        }

        return totalResourceIntervals
      },
      employeeDemandChart () {
        const demandChart = {}

        if (this.allowShowResources) {
          const taskPlanning = this.taskPlanningForDayFilter
          if (!taskPlanning) return

          const steps = 75
          const chartData = {
            chart: {
              animation: false,
              type: 'area',
              height: 75,
              width: (taskPlanning.totalMinutes + 20) * this.pixelsPerMinute,
              backgroundColor: 'transparent'
            },
            plotOptions: {
              area: {
                connectNulls: true,
                stacking: 'normal',
                marker: {
                  enabled: false
                }
              }
            },
            xAxis: {
              labels: {
                enabled: false
              },
              title: {
                text: null
              },
              visible: false,
              startOnTick: false,
              endOnTick: false,
              tickPositions: []
            },
            yAxis: {
              endOnTick: false,
              startOnTick: false,
              labels: {
                enabled: false
              },
              title: {
                text: null
              },
              min: 0,
              tickPositions: [0],
              visible: false
            },
            credits: {
              enabled: false
            },
            legend: {
              enabled: false
            },
            title: {
              text: null
            },
            series: [
              {
                animation: false,
                name: 'Concessions',
                color: 'hsl(0, 65%, 40%)',
                fillColor: 'hsl(0, 65%, 55%)',
                data: _.map(taskPlanning.registerEmployeeDemandSeries, (value, index) => { return value < 0.05 ? null : Math.round(value * 100) / 100 }),
                zones: _.map(_.range(steps), index => {
                  const value = index / steps * maxTotalResourceHeatMapValue
                  return {
                    value,
                    color: 'hsl(' + (100 - Math.min(value * 100 / maxTotalResourceHeatMapValue, 100)) + ', 65%, 40%)',
                    fillColor: 'hsl(' + (100 - Math.min(value * 100 / maxTotalResourceHeatMapValue, 100)) + ', 65%, 55%)'
                  }
                }),
                zoneAxis: 'y'
              },
              {
                animation: false,
                name: 'Cleaning',
                color: 'rgb(253, 147, 8)',
                data: _.map(taskPlanning.cleaningEmployeesSeries, (value, index) => { return value < 0.05 ? null : Math.round(value * 100) / 100 })
              }
            ]
          }

          demandChart.style = {
            top: '50px',
            left: ((timeToMinutes(taskPlanning.siteStartTime) - 10) * this.pixelsPerMinute) + 'px'
          }
          demandChart.chartData = chartData
        }

        return demandChart
      },
      arriveCharts () {
        const arriveCharts = []

        const maxArriveValue = _(this.displaySchedule).map(showingSlot => {
          const showing = showingSlot.showing
          if (!showing) return

          const taskPlanning = this.taskPlanningForDayFilter
          if (!taskPlanning) return

          return taskPlanning.arriveSeriesByShowingId[showing.id]
        }).compact().flatten().max()

        _.each(this.displaySchedule, showingSlot => {
          const showing = showingSlot.showing
          if (!showing) return

          const taskPlanning = this.taskPlanningForDayFilter
          if (!taskPlanning) return

          const arriveSeries = taskPlanning.arriveSeriesByShowingId[showing.id]
          if (!arriveSeries) return

          const screenIndex = this.screenIndex(showing.screen.id)

          const resourceGraphHeight = this.resourceGraphHeight

          const yOffset = 90
          let top
          if (this.by === 'screen') {
            top = yOffset + screenIndex * 7 * totalItemHeight
          } else {
            top = yOffset +
              screenIndex * totalItemHeight + resourceGraphHeight
          }

          let cummulatedArrived = 0

          const startOffset = timeToMinutes(showing.time) - (timeToMinutes(taskPlanning.siteStartTime) + taskPlanning.arriveSeriesByShowingStartMinute[showing.id])

          const chartData = {
            chart: {
              animation: false,
              type: 'area',
              height: 50,
              width: ARRIVE_CHART_WIDTH * this.pixelsPerMinute,
              backgroundColor: 'transparent'
            },
            xAxis: {
              labels: {
                enabled: false
              },
              title: {
                text: 'min'
              },
              visible: false,
              startOnTick: false,
              endOnTick: false,
              tickPositions: []
            },
            yAxis: {
              endOnTick: false,
              startOnTick: false,
              labels: {
                enabled: false
              },
              title: {
                text: null
              },
              min: 0,
              max: maxArriveValue,
              tickPositions: [0],
              visible: false
            },
            credits: {
              enabled: false
            },
            legend: {
              enabled: false
            },
            title: {
              text: null
            },
            tooltip: {
              valueSuffix: ''
            },
            series: [{
              animation: false,
              name: 'Labor',
              data: _.map(arriveSeries, (value, index) => {
                cummulatedArrived += value
                return {
                  name: (index - startOffset) + ' min, ' + Math.round(cummulatedArrived) + ' people',
                  y: (value / taskPlanning.serviceRate) < 0.05 ? null : Math.round(value * 100) / 100
                }
              }),
              color: showing.movie.color
            }]
          }

          arriveCharts.push({
            style: {
              top: top + 'px',
              left: ((timeToMinutes(taskPlanning.siteStartTime) + taskPlanning.arriveSeriesByShowingStartMinute[showing.id]) * this.pixelsPerMinute) + 'px'
            },
            showingId: showing.id,
            chartData
          })
        })

        return arriveCharts
      },
      cleanTaskBoxes () {
        return this.cleanTaskBoxesWorker(false)
      },
      cleanTasksScheduled () {
        return this.cleanTasksScheduledWorker(false)
      },
      cleanTaskBoxesSummary () {
        return this.cleanTaskBoxesWorker(true)
      },
      cleanTasksScheduledSummary () {
        return this.cleanTasksScheduledWorker(true)
      },
      resourceGraphHeight () {
        if (this.showResources) {
          return resourceGraphHeight
        } else {
          return 0
        }
      },
      siteSchedule () {
        return this.config.manualSchedule ? this.customSchedule : this.defaultSiteSchedule?.schedule || {}
      },
      fillTitleCount () {
        let count = 0

        _.each(this.planElements.data, (planElement) => {
          if (planElement.config.customSiteSchedule) {
            return
          }
          count += planElement.titleCount
        })

        if (this.config.screenTitleOneTwice) {
          count += 1
        }

        return count
      },
      planElementNameToBookingWeekMoviesMap () {
        const planElementNameToBookingWeekMoviesMap = {}
        _.each(this.planElements?.data, planElement => {
          planElementNameToBookingWeekMoviesMap[planElement.name] = planElementNameToBookingWeekMoviesMap[planElement.name] || []
          _.each(planElement.bookingWeekMovies.data, bookingWeekMovie => {
            planElementNameToBookingWeekMoviesMap[planElement.name].push(bookingWeekMovie)
          })
        })
        return planElementNameToBookingWeekMoviesMap
      },
      finalSiteSchedule () {
        return this.config?.manualSiteSchedule ? this.custSiteSchedule : this.defaultSiteSchedule
      },
      computedLayoutData () {
        return this.buildComputedLayout(false)
      },
      plannedLayout () {
        return this.buildPlannedLayout()
      },
      displayLayout () {
        return this.buildDisplayLayout()
      },
      plannedLayoutWithoutActuals () {
        return this.buildPlannedLayoutWithoutActuals()
      },
      actualLayout () {
        return this.buildActualLayout()
      },
      actualScheduleData () {
        return this.buildActualSchedule(false)
      },
      displaySchedule () {
        return this.buildDisplaySchedule(this.plannedLayoutWithoutActuals)
      }
    }

    this.watchers = [
      [
        function () { return [this.allowShowResources] },
        function () {
          if (!this.allowShowResources && this.showResources) {
            this.showResources = false
          }
        }
      ],
      [
        function () { return [this.computedLayoutData, this.id] },
        function () {
          _(this.computedLayout).values().each(showingSlot => {
            showingSlot.destroy()
          })
          this.computedLayout = this.buildComputedLayout(true)
        },
        { immediate: true }
      ],
      [
        function () { return [this.paintValue, this.id] },
        function () {
          if (
            ((!this.paintValue?.movie) && this.showingSlotGhost) ||
            (this.paintValue?.movie && this.showingSlotGhost?.bookingWeekMovie?.movie && this.paintValue.movie.id !== this.showingSlotGhost.bookingWeekMovie.movie.id)
          ) {
            this.destroyShowingSlotGhost()
          }
        }
      ],
      [
        function () { return [this.actualScheduleData, this.id] },
        function () {
          const showingSlotByShowingId = _.keyBy(this.actualSchedule, 'showing.id')
          this.actualSchedule = this.buildActualSchedule(true, showingSlotByShowingId)
          // We have re-built the schedule, for any showing slots that are no longer in the schedule
          // Now we will destroy those left over showing slots
          const showingSlotsToDestroy = _.differenceBy(_.values(showingSlotByShowingId), this.actualSchedule, 'showing.id')
          _.each(showingSlotsToDestroy, slotToDestroy => slotToDestroy.destroy())
        },
        { immediate: true }
      ]
    ]
  }

  cleanTaskBoxesWorker (summary = false) {
    const cleanTaskBoxes = []

    if (this.allowShowResources) {
      const taskPlanning = this.taskPlanningForDayFilter
      if (!taskPlanning) return
      _.each(taskPlanning.cleanTasks.data, cleanTask => {
        const screenIndex = this.screenIndex(cleanTask.showing.screen.id)

        const resourceGraphHeight = this.resourceGraphHeight

        let yOffset
        let top
        if (summary) {
          yOffset = 9
          top = yOffset +
              screenIndex * 10 + resourceGraphHeight
        } else {
          yOffset = 94
          if (this.by === 'screen') {
            top = yOffset + screenIndex * 7 * totalItemHeight
          } else {
            top = yOffset +
              screenIndex * totalItemHeight + resourceGraphHeight
          }
        }

        cleanTaskBoxes.push({
          top: top + 'px',
          left: (timeToMinutes(cleanTask.siteEarliestStartTime) * this.pixelsPerMinute) + 'px',
          width: ((timeToMinutes(cleanTask.siteDueTime) - timeToMinutes(cleanTask.siteEarliestStartTime)) * this.pixelsPerMinute + 4) + 'px',
          height: summary ? '9px' : '20px',
          border: summary ? 'solid 1px #FD9308' : 'solid 1px #FD9308',
          duration: cleanTask.estimatedDuration
        })
      })
    }

    return cleanTaskBoxes
  }

  cleanTasksScheduledWorker (summary = false) {
    const cleanTaskBoxes = []

    if (this.allowShowResources) {
      const taskPlanning = this.taskPlanningForDayFilter
      if (!taskPlanning) return
      _.each(taskPlanning.cleanTasks.data, cleanTask => {
        const screenIndex = this.screenIndex(cleanTask.showing.screen.id)

        const resourceGraphHeight = this.resourceGraphHeight

        let yOffset
        let top
        if (summary) {
          yOffset = 11
          top = yOffset +
            screenIndex * 10 + resourceGraphHeight
        } else {
          yOffset = 97
          if (this.by === 'screen') {
            top = yOffset + screenIndex * 7 * totalItemHeight
          } else {
            top = yOffset +
                screenIndex * totalItemHeight + resourceGraphHeight
          }
        }

        cleanTaskBoxes.push({
          top: top + 'px',
          left: (timeToMinutes(cleanTask.siteScheduledStartTime) * this.pixelsPerMinute + 2) + 'px',
          width: cleanTask.estimatedDuration * this.pixelsPerMinute + 'px',
          height: summary ? '5px' : '14px',
          background: '#FD9308',
          border: 'solid 1px #FD9308',
          duration: cleanTask.estimatedDuration
        })
      })
    }

    return cleanTaskBoxes
  }

  validateShowingSlotGhost (dayNumber, screenId, left) {
    if (this.auditView) return

    if (!this.showingSlotGhost) {
      this.showingSlotGhost = new ShowingSlot({
        timeSlot: this.configuration.timeSlots.data[0],
        bookingWeekMovie: this.paintValue,
        bookingWeek: this
      })
    }

    this.showingSlotGhost.bookingWeekMovie = this.paintValue
    this.showingSlotGhost.screenId = screenId
    this.showingSlotGhost.dayNumber = dayNumber
    this.showingSlotGhost.ghostSlot = true

    // Compute time for cursor posistion
    const cursorOffset = 50 // Draw the showing slot slightly offset from the cursor so we don't cover up the start time
    const time = date.adjustDate(this.date, { hours: START_HOUR, minutes: ((left - cursorOffset) / this.pixelsPerMinute) })
    const days = _.indexOf(this.configuration.currentCircuit.daysOfWeekNumbers, dayNumber)
    const timeZoneOffset = this.getOffsetMinutes(this.configuration.currentSite.timeZone, time) - this.getOffsetMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone, time)

    // Set round to Snap To minutes
    const currentMinutes = timeToMinutes(time)
    const snapTo = this.snapTo || 1
    const roundedMinutes = Math.round(currentMinutes / snapTo) * snapTo

    // Adjust for timezone and Snap To
    this.showingSlotGhost.time = new IsoDateTime(date.addToDate(time, { days, minutes: -timeZoneOffset + (roundedMinutes - currentMinutes) }))
  }

  destroyShowingSlotGhost () {
    this.showingSlotGhost?.destroy()
    this.showingSlotGhost = null
  }

  beforeDestroy () {
    _.each(this.actualSchedule, showingSlot => {
      showingSlot.destroy()
    })

    _.each(this.computedLayout, showingSlot => {
      showingSlot.destroy()
    })

    this.destroyShowingSlotGhost()
  }

  // The current layout given all setting and any customization, ignoring actual scheduled showings
  buildPlannedLayout () {
    if (this.config?.manualLayout) {
      if (this.previewingAction) {
        return this.previewLayout || {}
      } else {
        return this.customLayoutMap || {}
      }
    } else {
      return this.computedLayout || {}
    }
  }

  // Algorithmic layout
  buildComputedLayout (models = true) {
    const layout = {}

    if (!this.planElements) return {}

    // First find planning elements with custom site schedule. Schedule these together first.
    // Treat each timeslot as a single competitive resource and the screens as the supply. Doesn't support screen splitting.
    _.each(this.configuration.timeSlots.data, (timeSlot) => {
      _.each(this.configuration.currentCircuit.daysOfWeekNumbers, (dayNumber) => {
        const bookingWeekMoviesToPlace = []
        _.each(this.planElements.data, (planElement) => {
          if (!planElement.config.customSiteSchedule) {
            return
          }
          if (planElement.customSiteSchedule && planElement.customSiteSchedule[timeSlot.id] && planElement.customSiteSchedule[timeSlot.id][dayNumber]) {
            _.each(planElement.numbers, titleNumber => {
              bookingWeekMoviesToPlace.push(planElement.bookingWeekMovies.data[titleNumber - 1])
            })
          }
        })
        let screenIndex = 0
        while (bookingWeekMoviesToPlace.length && screenIndex < this.configuration.screens.length) {
          const bookingWeekMovie = bookingWeekMoviesToPlace.shift()
          const screen = this.configuration.screens.data[screenIndex]

          const data = {
            timeSlotId: timeSlot.id,
            screenId: screen.id,
            dayNumber,

            bookingWeekMovie,

            bookingWeek: this
          }

          let showingSlot
          if (models) {
            showingSlot = new ShowingSlot(data)
          } else {
            showingSlot = data
          }

          const key = showingSlotKey(screen.id, timeSlot.id, dayNumber)
          layout[key] = showingSlot

          screenIndex += 1
        }
      })
    })

    // Second take everything that is has no site schedule and just fill the remaining space in priority order and splitting screens as needed.

    // Get the total list of titles to place
    const titlesToPlace = []
    let addedSecondScreening = false
    _.each(this.planElements.data, (planElement) => {
      if (planElement.config.customSiteSchedule || !planElement.bookingWeekMovies?.data) {
        return
      }

      _.each(planElement.numbers, titleNumber => {
        const bookingWeekMovie = planElement.bookingWeekMovies.data[titleNumber - 1]
        titlesToPlace.push(bookingWeekMovie)
        if (this.config.screenTitleOneTwice && !addedSecondScreening) {
          titlesToPlace.push(bookingWeekMovie)
          addedSecondScreening = true
        }
      })
    })

    // Determine screen splitting if needed
    let screenTitlesList = []
    // const totalTitleCount = titlesToPlace.length
    titlesToPlace.reverse()
    let screensLeft = this.configuration.screens.data.length
    _.each(_.clone(this.configuration.screens.data).reverse(), (screen, reverseIndex) => {
      const titlesLeft = titlesToPlace.length
      let countForScreen
      if (titlesLeft < screensLeft) {
        countForScreen = 0
      } else {
        countForScreen = Math.ceil(titlesLeft / screensLeft)
      }
      let titlesToPlaceToScreen = null
      if (countForScreen > 0) {
        titlesToPlaceToScreen = titlesToPlace.splice(0, countForScreen)
      }
      screenTitlesList[this.configuration.screens.length - reverseIndex - 1] = titlesToPlaceToScreen
      screensLeft -= 1
    })

    if (this.config.allowSimultaneousScreenings) {
      screenTitlesList = _.compact(screenTitlesList)
    }

    // Do placement
    // Keep track of what we placed
    // const titleNumberByDayTimeSlot = {}
    _.each(this.configuration.screens.data, (screen, screenIndex) => {
      _.each(this.configuration.timeSlots.data, (timeSlot, timeSlotIndex) => {
        _.each(this.configuration.currentCircuit.daysOfWeekNumbers, (dayNumber, dayNumberIndex) => {
          // Honor site schedule
          if (!this.isShowtimeOpen(timeSlot.id, dayNumber)) {
            return
          }

          // Don't override existing placements
          const key = showingSlotKey(screen.id, timeSlot.id, dayNumber)
          if (layout[key]) {
            return
          }

          const titleList = screenTitlesList[screenIndex % screenTitlesList.length]
          if (!titleList) {
            return
          }
          const titleIndex = this.config.rotateTimesOnSplitScreens ? (timeSlotIndex + dayNumberIndex) : timeSlotIndex
          const bookingWeekMovie = titleList[titleIndex % titleList.length]

          const data = {
            timeSlotId: timeSlot.id,
            screenId: screen.id,
            dayNumber,

            bookingWeekMovie,

            bookingWeek: this
          }

          let showingSlot
          if (models) {
            showingSlot = new ShowingSlot(data)
          } else {
            showingSlot = data
          }

          layout[key] = showingSlot
        })
      })
    })

    return layout
  }

  // Planned layout with actual scheduled showings superseding the planned layout
  buildDisplayLayout () {
    return this.combineLayoutWorker(this.layoutMode)
  }

  // Mode options include 'plan', 'plan-and-actual, 'actual'
  combineLayoutWorker (mode) {
    let layout
    if (mode === 'plan' || mode === 'plan-and-actual') {
      layout = _.clone(this.plannedLayout)
    } else {
      layout = {}
    }

    if (mode === 'plan-and-actual' || mode === 'actual') {
      _.extend(layout, this.actualLayout)
    }

    return layout
  }

  // Planned layout with actual scheduled showings superseding the planned layout
  buildPlannedLayoutWithoutActuals () {
    const actualLayoutKeys = _.keys(this.combineLayoutWorker('actual'))
    return _.values(_.pickBy(this.combineLayoutWorker('plan'), (_value, key) => !_.includes(actualLayoutKeys, key)))
  }

  // Planned layout with actual scheduled showings superseding the planned layout
  buildActualLayout () {
    // TODO, support multiple showings in one slot????
    const actualLayout = {}

    _.each(this.actualSchedule, showingSlot => {
      actualLayout[showingSlot.layoutKey] = showingSlot
    })

    return actualLayout
  }

  buildActualSchedule (models = false, showingIdToShowingMap = {}) {
    if (!this.showings) {
      return []
    }
    return _.map(this.showings.data, showing => {
      if (models) {
        let showingSlot
        if (showingIdToShowingMap[showing.id]) {
          showingSlot = showingIdToShowingMap[showing.id]
        }

        if (showingSlot && showingSlot?.showing !== showing) {
          showingSlot.showing = showing
        }

        if (!showingSlot || showingSlot.destroyed) {
          showingSlot = new ShowingSlot({
            showing,
            bookingWeek: this,
            bookingWeekMovie: showing.bookingWeekMovie
          })
        }
        return showingSlot
      } else {
        return [showing.id, showing === showingIdToShowingMap[showing.id]?.showing]
      }
    })
  }

  buildDisplaySchedule (plannedLayoutWithoutActuals) {
    let schedule
    if (this.scheduleMode === 'actual' || this.showResources) {
      schedule = this.actualSchedule
    } else if (this.scheduleMode === 'plan-and-actual') {
      schedule = this.actualSchedule?.concat(plannedLayoutWithoutActuals)
    } else if (this.scheduleMode === 'published') {
      schedule = _.filter(this.actualSchedule, showingSlot => showingSlot.showing.published)
    } else {
      // throw new Error('Unhandled schedule mode: ' + this.scheduleMode)
    }

    return _.filter(schedule, showingSlot => {
      if (this.by === 'screen' && this.screenFilter !== null && showingSlot.screen.id !== this.screenFilter) {
        return false
      }
      if (this.by === 'day' && this.dayFilter !== null && +showingSlot.dayNumber !== this.dayFilter) {
        return false
      }

      if (this.auditView === 'start-times') {
        return _.some(
          this.filteredScreens,
          screen =>
            +showingSlot.dayNumber === this.dayFilter &&
            screen.id === showingSlot.showing?.screenId
        )
      }

      return true
    })
  }

  get completeSchedule () {
    return this.actualSchedule?.concat(_.values(this.combineLayoutWorker('plan'))) || []
  }

  handleNewShowing (showing) {
    let bookingWeekMovie

    showing.bookingWeek = this

    _.some(this.planElements?.data, planElement => {
      bookingWeekMovie = _.find(planElement.bookingWeekMovies?.data, bookingWeekMovie => {
        return bookingWeekMovie && bookingWeekMovie.movie && bookingWeekMovie.movie?.id === showing.movie?.id
      })
      return bookingWeekMovie
    })

    if (!bookingWeekMovie) {
      this.addUnplannedMovieFromShowing(showing)
    }
  }

  addUnplannedMovieFromShowing (showing) {
    if (!this.unplannedPlanElement) {
      this.unplannedPlanElement = new PlanElement({ name: 'Unplanned', color: '#999', bookingWeekMovies: [], titleCount: 0 })
      this.planElements.add(this.unplannedPlanElement)
    }

    const bookingWeekMovie = new BookingWeekMovie({
      movie: showing.movie,
      priorityOrder: this.unplannedPlanElement.titleCount,
      planElement: this.unplannedPlanElement
    })
    this.unplannedPlanElement.bookingWeekMovies.add(bookingWeekMovie)
    this.unplannedPlanElement.titleCount += 1

    return bookingWeekMovie
  }

  isShowtimeOpen (timeSlotId, dayNumber) {
    if (!this.siteSchedule) return false
    return this.siteSchedule[timeSlotId] && this.siteSchedule[timeSlotId][dayNumber]
  }

  async saveBookingWeek (callback) {
    // If we have any unsaved plan elements or booking week movies,
    // we won't be able to resolve them when saving showings - save them early instead.
    // In the future showings can become children of booking week movies instead of booking weeks.
    if (_.some(this.planElements.data, planElement => planElement.isLocal || _.some(planElement.bookingWeekMovies.data, 'isLocal'))) {
      // Save the local information of booking week movies we are saving against showings
      const localBookingWeekMovies = {}
      _.each(this.planElements.data, planElement => {
        _.each(planElement.bookingWeekMovies.data, bookingWeekMovie => {
          if (bookingWeekMovie.isLocal) {
            localBookingWeekMovies[bookingWeekMovie.id] = bookingWeekMovie
          }
        })
      })
      _.each(this.showings.data, showing => {
        if (showing.bookingWeekMovie && showing.bookingWeekMovie.isLocal) {
          showing.bookingWeekMovieName = localBookingWeekMovies[showing.bookingWeekMovie.id].description
        }
      })
      // Remove the showings so only booking week movies are saved
      this.showings.preventDestroyIds = this.showings.data.map(showing => showing.id)
      const showingsData = this.showings.data
      this.showings.data = []
      const results = await this.save()
      _.each(this.showings.data, showing => showing.destroy())
      this.showings.data = showingsData

      if (results.errors?.length) {
        // If there was an error, abort
        return results
      }

      // Map saved booking week movies to local versions
      const bookingWeekMovieNames = {}
      _.each(this.planElements.data, planElement => {
        _.each(planElement.bookingWeekMovies.data, bookingWeekMovie => {
          bookingWeekMovieNames[bookingWeekMovie.description] = bookingWeekMovie
        })
      })
      _.each(this.showings.data, showing => {
        if (showing.bookingWeekMovie && showing.bookingWeekMovie.isLocal) {
          showing.bookingWeekMovie = bookingWeekMovieNames[showing.bookingWeekMovieName]
        }
      })

      // Continue to normal booking week save logic
    }

    // We can skip batch saving if we have less than 100 showings as it increases the save time
    if (this.showings.data.length < 100) {
      return await this.save()
    } else {
      // We are batch saving
      this.batchSavingInProgress = true
    }
    const daysSuccessfullySaved = []

    // We need to save in batches because the server can't handle the payload size
    // We group by day and save each day in a separate batch
    const batches = _.groupBy(this.showings.data, showing => showing.dayNumber)
    // We also group the original data by day so we can update the collection at each day batch
    const batchesOriginalData = _.groupBy(this.showings.originalData, showing => new IsoDateTime(addToDate(showing.time, { hours: -4 })).inTimeZone(this.configuration.currentSite?.timeZone).getDay())
    // We only need to process days where showings did or now exist
    const daysToProcess = _.uniq(_.concat(_.keys(batches), _.keys(batchesOriginalData)))
    // Keep reference to original length to calculate percentage
    const originalBatchesLength = daysToProcess.length

    // Prevent GQL data response until last batch processed
    this.preventResults = daysToProcess.length > 1

    // We need to prevent destroy of showings that still exist in our current colleciton to prevent deleting them
    this.showings.preventDestroyIds = this.showings.data.map(showing => showing.id)
    // Get the next day to process
    let currentProcessingDay = daysToProcess.shift()
    // Set the current showings to the first batch
    this.showings.data = batches[currentProcessingDay] || []
    // Set the current original data to the first batch
    this.showings.originalData = batchesOriginalData[currentProcessingDay] || []
    // Keep track of the current batch number to calculate percentage
    let batchCounter = 0

    const results = await this.save(() => {
      this.preventEncodingPlanElements = true
      // Calculate and notify the current progress percentage
      daysSuccessfullySaved.push(currentProcessingDay)
      const currentBatchNumber = ++batchCounter
      const currentProgressPercent = Math.round(currentBatchNumber / originalBatchesLength * 100)
      callback(currentProgressPercent)

      this.preventResults = daysToProcess.length > 1

      // If there are no more days to process, we are done
      if (daysToProcess.length === 0) return false

      // Otherwise, we need to process the next day
      currentProcessingDay = daysToProcess.shift()

      // Set the current showings to the next batch
      this.showings.originalData = batchesOriginalData[currentProcessingDay] || []
      this.showings.data = batches[currentProcessingDay] || []

      // Return true to continue saving next batch
      return true
    })

    this.preventEncodingPlanElements = false

    // Since we are saving in batches, this means we commit at the end of each batch
    // We need to fetch the data again to get the latest changes and merge them with the current collection
    if (results.errors?.length && daysSuccessfullySaved.length > 0) {
      // Figure out which days were not saved
      const unsavedDays = _.difference(Object.keys(batches), daysSuccessfullySaved)

      // Fetch the current data
      await this.fetch(true)

      // Add all saved days to array
      let mergedShowingData = _.filter(this.showings.data, showing => !_.includes(unsavedDays, showing.dayNumber))

      // Add all unsaved days to array
      _.each(unsavedDays, day => {
        mergedShowingData = [...mergedShowingData, ...batches[day]]
      })

      // Set the showing data to the merged data
      this.showings.data = mergedShowingData

      this.batchSavingInProgress = false
    }

    return results
  }

  screenIndex (screenId) {
    return _.indexOf(_.map(this.filteredScreens, 'id'), +screenId)
  }

  dayIndex (dayNumber) {
    return _.indexOf(this.daysOfWeekNumbers, +dayNumber)
  }

  initializeActualLayout () {
    _.each(this.showings && this.showings.data, showing => {
      this.handleNewShowing(showing)
    })
  }

  clearCurrentSelection () {
    _.each(this.completeSchedule, scheduleSlot => { scheduleSlot.currentSelection = false })
  }

  clearFinalSelection () {
    _.each(this.completeSchedule, scheduleSlot => { scheduleSlot.finalSelection = false })
  }

  clearSelection () {
    this.clearCurrentSelection()
    this.clearFinalSelection()
  }

  finalizeCurrentSelection () {
    _.each(this.completeSchedule, scheduleSlot => {
      scheduleSlot.finalSelection = scheduleSlot.finalSelection || scheduleSlot.currentSelection
      scheduleSlot.currentSelection = false
    })
  }

  selectBy ({ screenId = null, dayNumber = null, movieId = null }) {
    this.clearSelection()
    _(this.displaySchedule).filter(showingSlot => {
      return (screenId === null || showingSlot?.screen?.id === screenId) &&
        (dayNumber === null || showingSlot.dayNumber === dayNumber) &&
        (movieId === null || showingSlot?.movie?.id === movieId)
    }).each(showingSlot => { showingSlot.finalSelection = true })
  }

  copyPriorDay (dayNumber) {
    const priorDayNumber = validateDayNumber(dayNumber - 1)

    _(this.completeSchedule).filter(showingSlot => {
      if (this.by === 'screen' && !_.isNil(this.screenFilter)) {
        return showingSlot.dayNumber === priorDayNumber && showingSlot?.screen?.id === this.screenFilter
      } else {
        return showingSlot.dayNumber === priorDayNumber
      }
    }).each(showingSlot => {
      if (!showingSlot.showing) return

      const newShowing = showingSlot.showing.copy()
      newShowing.published = false
      newShowing.ticketsSold = 0
      newShowing.setDayNumber(dayNumber, this.startDayNumber)
      this.showings.add(newShowing)
    })
  }

  publishSelectedShowings () {
    this.publishShowings(this.selected)
  }

  publishDisplayedShowings () {
    if (this.selected.length > 0) {
      this.publishShowings(this.selected)
    } else {
      this.publishShowings(this.displaySchedule)
    }
  }

  get publishDisplayedShowingsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.displaySchedule, showingSlot => !(!showingSlot.showing || showingSlot.showing.published)).length
  }

  publishShowings (showingSlots) {
    _.each(showingSlots, showingSlot => {
      if (!showingSlot.showing) return

      showingSlot.showing.published = true
    })
  }

  unpublishSelectedShowings () {
    this.unpublishShowings(this.selected)
  }

  unpublishDisplayedShowings () {
    if (this.selected.length > 0) {
      this.unpublishShowings(this.selected)
    } else {
      this.unpublishShowings(this.displaySchedule)
    }
  }

  get unpublishDisplayedShowingsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.displaySchedule, showingSlot => !(!showingSlot.showing || !showingSlot.showing.published)).length
  }

  unpublishShowings (showingSlots) {
    _.each(showingSlots, showingSlot => {
      if (!showingSlot.showing) return

      showingSlot.showing.published = false
    })
  }

  convertSelectedToShowings () {
    this.convertToShowings(this.selectedWithoutShowings)
  }

  convertDisplayedToShowings () {
    if (this.selected.length > 0) {
      this.convertToShowings(this.selectedWithoutShowings)
    } else {
      this.convertToShowings(this.displaySchedule)
    }
  }

  get convertDisplayedToShowingsCount () {
    return _.filter(this.selected.length > 0 ? this.selectedWithoutShowings : this.displaySchedule, showingSlot => !showingSlot.showing && (showingSlot.movie && showingSlot.movie.isRemote)).length
  }

  convertToShowings (showingSlots) {
    _.each(showingSlots, showingSlot => {
      if (showingSlot.showing || !showingSlot.movie?.isRemote) {
        return
      }
      this.showings.add(showingSlot.convertToShowing())
    })
  }

  removeSelectedShowings () {
    this.removeShowings(this.selected)
  }

  removeDisplayedShowings () {
    if (this.selected.length > 0) {
      this.removeShowings(this.selected)
    } else {
      this.removeShowings(this.displaySchedule)
    }
  }

  canRemoveShowing (showing) {
    if (_.isNil(showing)) return false

    if (showing.published) return false

    if (showing.ticketsExist) return false

    return true
  }

  get removeDisplayedShowingsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.displaySchedule, showingSlot => this.canRemoveShowing(showingSlot?.showing)).length
  }

  removeShowings (showingSlots) {
    // Pushing to array to avoid mutating the showings collection while iterating to see a massive performance improvement
    const showingsToRemove = []
    _.each(showingSlots, showingSlot => {
      if (this.canRemoveShowing(showingSlot?.showing)) showingsToRemove.push(showingSlot.showing)
    })
    this.showings.removeAll(showingsToRemove)
  }

  removeSelectedFromPlan () {

  }

  alignStart () {
    const startTime = _.sortBy(this.selectedWithEditableTime, showingSlot => showingSlot.startTimeMinutes)[0].showing.time
    _.each(this.selectedWithEditableTime, showingSlot => {
      showingSlot.showing.time = startTime
    })
  }

  alignEnd () {
    const endTimeMinutes = _.sortBy(this.selectedWithEditableTime, showingSlot => -(showingSlot.startTimeMinutes + showingSlot.totalDurationMinutes))[0].endTimeMinutes

    _.each(this.selectedWithEditableTime, showingSlot => {
      showingSlot.showing.addMinutes((endTimeMinutes - showingSlot.totalDurationMinutes) - showingSlot.startTimeMinutes)
    })
  }

  snapToMinutes (minutes, showingSlots) {
    if (!showingSlots) {
      showingSlots = this.selectedWithEditableTime
    }
    if (!minutes) {
      minutes = this.snapTo || 5
    }
    _.each(showingSlots, showingSlot => {
      const currentMinutes = timeToMinutes(showingSlot.showing.time)
      const roundedMinutes = Math.round(currentMinutes / minutes) * minutes
      showingSlot.showing.addMinutes(roundedMinutes - currentMinutes)
    })
  }

  spaceEvenly () {
    _(this.selectedWithEditableTime).groupBy(showingSlot => `${showingSlot.screen.id},${showingSlot.dayNumber}`).filter(groupedShowingSlots => groupedShowingSlots.length > 1).each((groupedShowingSlots) => {
      const showingSlots = _.sortBy(groupedShowingSlots, showingSlot => showingSlot.startTimeMinutes)
      let gapMinutes = 0
      let priorEndMinutes = null
      _.each(showingSlots, showingSlot => {
        if (priorEndMinutes) {
          gapMinutes += showingSlot.startTimeMinutes - priorEndMinutes
        }
        priorEndMinutes = showingSlot.endTimeMinutes
      })

      const evenGap = gapMinutes / (groupedShowingSlots.length - 1)
      priorEndMinutes = null
      _.each(showingSlots, showingSlot => {
        if (priorEndMinutes) {
          showingSlot.showing.addMinutes((priorEndMinutes + evenGap) - showingSlot.startTimeMinutes)
          if (this.snapTo) {
            this.snapToMinutes(this.snapTo, [showingSlot])
          }
        }
        priorEndMinutes = showingSlot.endTimeMinutes
      })
    })
  }

  canRemoveMovie (movie, planElementName = null) {
    return !_.some(this.showings.data, showing => showing.movie?.id === movie.id && showing.bookingWeekMovie?.planElement?.name === planElementName)
  }

  canRemovePlanElement (planElement) {
    return _.every(planElement.bookingWeekMovies.data, bookingWeekMovie => !bookingWeekMovie.movie || this.canRemoveMovie(bookingWeekMovie.movie))
  }

  encode () {
    if (this.customLayoutMap) {
      this.buildLayoutFromLayoutMap()
    }

    return this.data
  }

  afterInflate () {
    this.inflateCustomLayoutMapFromCustomLayout()

    this.initializeActualLayout()

    this.batchSavingInProgress = false
  }

  inflateCustomLayoutMapFromCustomLayout () {
    const layout = {}
    _.each(this.customLayout, (layoutSlot, key) => {
      if (!layoutSlot) {
        return
      }

      const { screenId, timeSlotId, dayNumber } = showingSlotKeyDecode(key)
      const titleNumber = layoutSlot.titleNumber
      const planElement = _.find(this.planElements.data, { id: +layoutSlot.planElementId })
      const screen = _.find(this.configuration.screens.data, { id: +screenId })
      const bookingWeekMovie = planElement?.bookingWeekMovies?.data[titleNumber - 1]
      const timeSlot = _.find(this.configuration.timeSlots.data, { id: +timeSlotId })

      const values = [
        planElement,
        titleNumber,
        bookingWeekMovie,
        screen,
        timeSlot,
        dayNumber
      ]

      // If any value is blank, then this isn't a complete layout slot (i.e. we are still waiting on data, a time slot has changed, etc.)
      if (_.some(values, value => value === null || value === undefined)) {
        return
      }

      const showingSlot = new ShowingSlot({
        timeSlotId: timeSlot.id,
        screenId: screen.id,
        dayNumber,

        bookingWeekMovie,

        bookingWeek: this
      })

      layout[showingSlot.layoutKey] = showingSlot
    })

    this.customLayoutMap = layout
  }

  buildLayoutFromLayoutMap () {
    const customLayout = {}

    _.each(this.customLayoutMap, (titleSlot, key) => {
      if (!titleSlot) {
        return
      }

      const value = {
        planElementId: titleSlot.planElement?.id,
        titleNumber: titleSlot.titleNumber
      }

      customLayout[key] = value
    })

    this.customLayout = customLayout
  }

  hasPlanElementTemplate (planElementTemplate) {
    return _.some(this.planElements?.data, planElement => planElement.parentPlanElement?.id === planElementTemplate.id)
  }

  addPlanElementTemplate (template) {
    if (this.hasPlanElementTemplate(template)) {
      return
    }

    const planElements = this.planElements.data

    const newPlanElement = new PlanElement({
      name: template.name,
      color: template.color,
      isTemplate: false,
      parentPlanElement: template,
      isCustom: false,
      default: template.default,
      displayOrder: planElements.length,
      titleCount: template.titleCount,
      customSiteSchedule: _.cloneDeep(template.customSiteSchedule),
      config: _.cloneDeep(template.config),
      overridePriceCard: template.overridePriceCard,
      bookingWeek: this,
      bookingWeekMovies: []
    })

    // We need to add these references separately because the constructor isn't handling them
    newPlanElement.customPriceCard = template.customPriceCard
    newPlanElement.showingBadges = template.showingBadges

    this.planElements.add(newPlanElement)
  }

  addCustomPlanElement () {
    this.planElements.add(new PlanElement({
      name: 'Custom',
      color: '#666',
      isTemplate: false,
      isCustom: true,
      default: false,
      displayOrder: this.planElements.data.length,
      titleCount: 1,
      bookingWeek: this,
      bookingWeekMovies: []
    }))
  }

  getOffsetMinutes (timeZone = 'UTC', date = new Date()) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))
    return (tzDate.getTime() - utcDate.getTime()) / 60 / 1000
  }
}
